/**
 * StudyLoader — Iter 193.5
 *
 * Centered, embedded loading experience for the Study page. Replaces
 * both the previous fixed-overlay progress bar (which sat behind the
 * sticky mobile header until the student scrolled) and the flickering
 * skeleton grid. Always visible immediately, always centered on the
 * viewport, always tells the student what is loading and how far
 * along we are.
 *
 * Props:
 *  - active      : boolean       Whether loading is in progress.
 *  - targetName  : string?       Name to display ("Biology Batch").
 *                                Falls back to "your Study library".
 *  - variant     : 'initial'|'switch'  Slightly different copy per state.
 *
 * Progress model — predictive easing:
 *  * 0 → 30% within 100 ms (network handshake)
 *  * 30 → 85% over ~800 ms (server work)
 *  * 85 → 95% at 0.5%/tick until active=false (long tail)
 *  * On complete, snaps to 100% then hides after 320 ms.
 */
import React, { useEffect, useState } from "react";
import { BookOpen, Loader2, ArrowRightLeft } from "lucide-react";

export default function StudyLoader({ active, targetName, variant = "initial" }) {
  const [pct, setPct] = useState(0);

  useEffect(() => {
    if (!active) {
      setPct(100);
      const t = setTimeout(() => setPct(0), 320);
      return () => clearTimeout(t);
    }
    setPct(0);
    const t1 = setTimeout(() => setPct(35), 60);
    const t2 = setTimeout(() => setPct(65), 260);
    const t3 = setTimeout(() => setPct(85), 700);
    const tick = setInterval(() => setPct((p) => (p < 95 ? p + 0.6 : p)), 220);
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      clearInterval(tick);
    };
  }, [active]);

  if (!active && pct === 0) return null;

  const isSwitch = variant === "switch";
  const label = targetName || (isSwitch ? "your next batch" : "your Study library");
  const Icon = isSwitch ? ArrowRightLeft : BookOpen;

  return (
    <div
      className="min-h-[60vh] w-full flex items-center justify-center px-6"
      data-testid="study-loader"
    >
      <div
        className="w-full max-w-[420px] rounded-[22px] p-6 md:p-7 text-center"
        style={{
          background: "var(--px-surface, #ffffff)",
          border: "1.5px solid rgba(37, 99, 235, 0.18)",
          boxShadow:
            "0 22px 44px -20px rgba(37, 99, 235, 0.22)," +
            "0 8px 18px -10px rgba(15, 23, 42, 0.10)",
        }}
      >
        {/* Icon halo */}
        <div className="mx-auto mb-4 relative" style={{ width: 76, height: 76 }}>
          <div
            className="absolute inset-0 rounded-full animate-ping-slow"
            style={{
              background: "radial-gradient(circle, rgba(59,130,246,0.30), transparent 65%)",
            }}
          />
          <div
            className="absolute inset-2 rounded-full flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #60a5fa 0%, #3b82f6 55%, #2563eb 100%)",
              boxShadow: "0 12px 26px -8px rgba(37, 99, 235, 0.55)",
            }}
          >
            <Icon size={26} color="#ffffff" strokeWidth={2.4} />
          </div>
          <div
            className="absolute inset-0 rounded-full border-2 border-transparent"
            style={{
              borderTopColor: "#2563eb",
              animation: "study-loader-spin 900ms linear infinite",
            }}
          />
        </div>

        {/* Copy */}
        <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] mb-1"
             style={{ color: "#2563eb" }}>
          {isSwitch ? "Switching batch" : (targetName ? "Loading" : "Loading study library")}
        </div>
        <h2
          className="text-[19px] md:text-[22px] font-black leading-tight mb-4"
          style={{ color: "var(--px-ink)" }}
          data-testid="study-loader-label"
        >
          {isSwitch ? (
            <>
              Opening{" "}
              <span
                className="inline-block align-baseline truncate max-w-[220px]"
                title={label}
                style={{ color: "#2563eb", verticalAlign: "middle" }}
              >
                {label}
              </span>
              …
            </>
          ) : targetName ? (
            <>
              Preparing{" "}
              <span
                className="inline-block align-baseline truncate max-w-[220px]"
                title={label}
                style={{ color: "#2563eb", verticalAlign: "middle" }}
              >
                {targetName}
              </span>
              …
            </>
          ) : (
            <>Preparing your Study library…</>
          )}
        </h2>

        {/* Progress bar */}
        <div
          className="h-[6px] w-full rounded-full overflow-hidden mb-2"
          style={{ background: "rgba(37, 99, 235, 0.10)" }}
        >
          <div
            className="h-full transition-[width] duration-[420ms] ease-out"
            style={{
              width: `${pct}%`,
              background:
                "linear-gradient(90deg, #60a5fa 0%, #2563eb 55%, #1d4ed8 100%)",
              boxShadow: "0 0 12px 0 rgba(37, 99, 235, 0.55)",
            }}
          />
        </div>
        <div className="flex items-center justify-between text-[11.5px]"
             style={{ color: "var(--px-ink-soft)" }}>
          <span className="inline-flex items-center gap-1.5">
            <Loader2 size={11} className="animate-spin" />
            Fetching curriculum
          </span>
          <span
            className="tabular-nums font-mono font-bold"
            style={{ color: "#2563eb" }}
            data-testid="study-loader-pct"
          >
            {Math.round(pct)}%
          </span>
        </div>
      </div>

      <style>{`
        @keyframes study-loader-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes study-loader-ping {
          0%   { transform: scale(0.85); opacity: 0.55; }
          70%  { transform: scale(1.20); opacity: 0.10; }
          100% { transform: scale(0.85); opacity: 0.55; }
        }
        .animate-ping-slow {
          animation: study-loader-ping 1600ms cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
}
